import { styled } from '@mui/material/styles';
import { Position } from '@turf/turf';
import useQueryReturn from 'hooks/useQueryReturn';
import CustomMap from 'DesignSystem/Map/CustomMap';
import { DEFAULT_ZOOM_TO_LOCATION_LEVEL } from 'DesignSystem/Map/utils/map.config';
import { useField, useFormikContext } from 'formik';
import { GET_PLACES } from 'graphql/secure/queries';
import React, { FC, useEffect, useState } from 'react';
import { MapEvent, MapLayerMouseEvent, MarkerDragEvent } from 'react-map-gl';

interface ISelectableMapProps {
  field: {
    name: string;
  };
  locationNameKey: string;
}

const MapContainer = styled('div')(() => ({
  width: '100%',
  height: '100%',
}));

const SelectableMap: FC<ISelectableMapProps> = ({ field: { name }, locationNameKey }) => {
  const { setFieldValue } = useFormikContext();
  const [{ value }, , { setValue }] = useField(name);
  const [mapRef, setMapRef] = useState<MapEvent['target'] | null>(null);

  const [markerCoordinates, setMarkerCoordinates] = useState<Position>();

  const [, , { setValue: setMapBoxId }] = useField<string | null>('mapboxId');

  const getLocation = useQueryReturn(GET_PLACES, {
    fetchPolicy: 'no-cache',
  });

  const request = (query: unknown) =>
    getLocation({
      variables: {
        query,
      },
    });

  useEffect(() => {
    // The default validation is run for name, that's why the field name is `name`
    // But to get the coordinates we have them stored in `locationCoordinates`
    const lat = value?.lat;
    const lng = value?.lng;
    if (lat && lng) {
      const newZoom = mapRef?.getZoom() || DEFAULT_ZOOM_TO_LOCATION_LEVEL;
      mapRef?.flyTo({
        center: [lng, lat],
        screenSpeed: 1,
        maxDuration: 1000,
        zoom: newZoom > DEFAULT_ZOOM_TO_LOCATION_LEVEL ? newZoom : DEFAULT_ZOOM_TO_LOCATION_LEVEL,
      });
      setMarkerCoordinates([lng, lat]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mapRef]);

  const handleMoveMarker = async (event: MapLayerMouseEvent | MarkerDragEvent) => {
    const {
      lngLat: { lng, lat },
    } = event;
    if (lat && lng) {
      const { data } = await request(`${lng},${lat}`);

      if (data.getPlaces.length !== 0) {
        const location = data.getPlaces[0];

        setMapBoxId(location.mapboxId);
        setFieldValue(locationNameKey, location.title);
      }

      setMarkerCoordinates([lng, lat]);
      setValue({
        lat,
        lng,
      });
    }
  };

  return (
    <MapContainer>
      <CustomMap
        markers={markerCoordinates ? [markerCoordinates] : undefined}
        markerOptions={{ draggable: true }}
        style={{
          height: '100%',
          width: '100%',
        }}
        config={{
          enableMapStyleToggle: true,
        }}
        onClick={handleMoveMarker}
        onMarkerDragEnd={handleMoveMarker}
        onMapLoad={setMapRef}
      />
    </MapContainer>
  );
};

export default SelectableMap;
